/**
 * WhatsApp URL builder.
 *
 * Some Indonesian ISPs / networks block certain WhatsApp entry-points:
 *   ✗ api.whatsapp.com  (blocked)
 *   ✗ web.whatsapp.com  (blocked in some networks)
 *
 * Strategy chosen by the product owner:
 *   1) Try the native URI scheme first  →  whatsapp://send?phone=<digits>&text=<msg>
 *      This directly opens the installed WhatsApp app on mobile & desktop
 *      without touching any blocked domain.
 *   2) Fallback to wa.me                →  https://wa.me/<digits>?text=<msg>
 *      wa.me is a short-link redirector that usually still resolves even
 *      when api.whatsapp.com / web.whatsapp.com are blocked.
 */

const digits = (p) => String(p || "").replace(/[^0-9]/g, "");

/** Native WhatsApp deep-link (opens the app directly). */
export function buildWhatsAppDeepLink(phone, message = "") {
  const num = digits(phone);
  const msg = encodeURIComponent(message);
  return `whatsapp://send?phone=${num}&text=${msg}`;
}

/** Universal wa.me fallback link. */
export function buildWaMeUrl(phone, message = "") {
  const num = digits(phone);
  const msg = encodeURIComponent(message);
  return `https://wa.me/${num}${msg ? `?text=${msg}` : ""}`;
}

/**
 * Primary link used for <a href="…"> so users can right-click / long-press.
 * We expose the wa.me link because it works even if the whatsapp:// handler
 * is not registered (e.g. browsers without WhatsApp installed).
 */
export function buildWhatsAppUrl(phone, message = "") {
  return buildWaMeUrl(phone, message);
}

/**
 * Open WhatsApp in the most reliable way.
 * 1. Attempt the whatsapp:// URI (native app).
 * 2. If the browser is still focused ~1200 ms later (meaning the OS did
 *    not hand the request off to an app), fall back to wa.me.
 */
export function openWhatsApp(phone, message = "") {
  const deepLink = buildWhatsAppDeepLink(phone, message);
  const fallback = buildWaMeUrl(phone, message);

  let handedOff = false;
  const onBlur = () => {
    handedOff = true;
  };
  window.addEventListener("blur", onBlur, { once: true });

  // Try the native app first via a hidden iframe – this avoids the
  // "This site can't be reached" error page some browsers show when the
  // whatsapp:// protocol is not handled.
  try {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = deepLink;
    document.body.appendChild(iframe);
    setTimeout(() => iframe.remove(), 2000);
  } catch (err) {
    console.error("[whatsapp] iframe deep-link failed:", err);
    // Fall through — the wa.me fallback below still runs.
  }

  // If nothing captured focus, open the wa.me fallback in a new tab.
  setTimeout(() => {
    window.removeEventListener("blur", onBlur);
    if (handedOff) return;
    try {
      const w = window.open(fallback, "_blank", "noopener,noreferrer");
      if (!w) window.location.href = fallback;
    } catch (err) {
      // Popup blocker / cross-origin restriction — force top-level navigation
      // as the last-resort fallback and log for observability.
      console.warn("[whatsapp] fallback window.open blocked, forcing top-level nav:", err);
      window.location.href = fallback;
    }
  }, 1200);
}
